import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./PulmonologyMotifChips.css";

const readOptions = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || "null");
    return Array.isArray(stored) ? stored.filter((item) => typeof item === "string") : fallback;
  } catch {
    return fallback;
  }
};

export default function PulmonologyMotifChips({
  patientId,
  group,
  defaultOptions,
  selected = [],
  labelForOption = (option) => option,
  valueForOption = (option) => option,
  onToggle,
  onRemove,
  filterText = "",
  matchesFilter,
}) {
  const { t } = useTranslation("pulmonology");
  const storageKey = `medilink.pulmonology.motifs.${encodeURIComponent(String(patientId || "unassigned"))}.${group}`;
  const defaultsJson = JSON.stringify(defaultOptions || []);
  const [options, setOptions] = useState(() => readOptions(storageKey, JSON.parse(defaultsJson)));
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(options));
    } catch {
      // Storage can be disabled by browser policy.
    }
  }, [options, storageKey]);

  const visibleOptions = options.filter((option) =>
    !filterText || (matchesFilter ? matchesFilter(option) : labelForOption(option).toLowerCase().includes(filterText.toLowerCase()))
  );

  const addMotif = (event) => {
    event.preventDefault();
    const motif = draft.trim();
    if (!motif) return;
    const existing = options.find((option) => labelForOption(option).toLocaleLowerCase() === motif.toLocaleLowerCase());
    if (existing) {
      onToggle?.(valueForOption(existing));
    } else {
      setOptions((current) => [...current, motif]);
      onToggle?.(valueForOption(motif));
    }
    setDraft("");
    setAdding(false);
  };

  const removeMotif = (option) => {
    setOptions((current) => current.filter((item) => item !== option));
    onRemove?.(valueForOption(option));
  };

  return (
    <div className="pulmo-motif-group">
      <div className="pulmo-chips">
        {visibleOptions.map((option) => {
          const value = valueForOption(option);
          const label = labelForOption(option);
          return (
              <span className={`pulmo-motif-item${selected.includes(value) ? " is-selected" : ""}`} key={option}>
              <button
                type="button"
                className={`pulmo-chip${selected.includes(value) ? " is-selected" : ""}`}
                aria-pressed={selected.includes(value)}
                onClick={() => onToggle?.(value)}
              >
                {label}
              </button>
              <button
                type="button"
                className="pulmo-motif-remove"
                aria-label={t("common.removeMotif", { name: label })}
                title={t("common.removeMotif", { name: label })}
                onClick={() => removeMotif(option)}
              >
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          );
        })}
        {adding ? (
          <form className="pulmo-motif-add-form" onSubmit={addMotif}>
            <input
              autoFocus
              value={draft}
              aria-label={t("common.motifPlaceholder")}
              placeholder={t("common.motifPlaceholder")}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
            />
            <button type="submit" aria-label={t("common.saveMotif")} title={t("common.saveMotif")}>
              <Plus size={14} aria-hidden="true" />
            </button>
          </form>
        ) : (
          <button type="button" className="pulmo-motif-add" onClick={() => setAdding(true)}>
            <Plus size={13} aria-hidden="true" /> {t("common.addMotif")}
          </button>
        )}
      </div>
    </div>
  );
}